/**
 * API Client - Pure JWT / Stateless
 * 
 * Pure JWT Pattern:
 * - Automatically attaches Authorization: Bearer <token> header
 * - Handles token refresh on 401 errors
 * - No cookie dependencies
 * 
 * Enhanced with:
 * - Request/Response timing for performance monitoring
 * - Request IDs for correlation with backend logs
 * - Production-safe structured logging
 */

import axios from 'axios';
import { logger } from '../utils/logger';
import { getAccessToken, getRefreshToken, saveTokens, clearTokens } from './auth';

// Decode JWT payload without validation (for reading exp claim client-side)
const decodeTokenExp = (token: string): number | null => {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded.exp ?? null;
  } catch {
    return null;
  }
};

// Extend Axios config to include our custom metadata
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    metadata?: {
      startTime: number;
      requestId: string;
    };
    _retry?: boolean; // Track if request has been retried
    _retryCount?: number;
  }
}

const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL || 'http://localhost:5005';

// Generate unique request ID for correlation with backend logs
const generateRequestId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Refresh token mutex - prevents concurrent refresh requests
let isRefreshing = false;
let refreshSubscribers: Array<{ resolve: (token: string) => void; reject: (error: unknown) => void }> = [];

const subscribeTokenRefresh = (resolve: (token: string) => void, reject: (error: unknown) => void) => {
  refreshSubscribers.push({ resolve, reject });
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach(({ resolve }) => resolve(token));
  refreshSubscribers = [];
};

const onTokenRefreshFailed = (error: unknown) => {
  refreshSubscribers.forEach(({ reject }) => reject(error));
  refreshSubscribers = [];
};

// Proactively refresh the token if it's about to expire (within 60 seconds)
const proactiveRefresh = async (): Promise<string | null> => {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  if (!accessToken || !refreshToken) return null;

  const exp = decodeTokenExp(accessToken);
  if (!exp) return null;

  const now = Math.floor(Date.now() / 1000);
  const timeLeft = exp - now;

  // If more than 60 seconds remain, no refresh needed
  if (timeLeft > 60) return null;

  // If already refreshing, wait for it
  if (isRefreshing) {
    return new Promise((resolve) => {
      subscribeTokenRefresh(
        (newToken: string) => resolve(newToken || null),
        () => resolve(null)
      );
    });
  }

  isRefreshing = true;
  try {
    logger.info('Proactively refreshing token', { timeLeft });
    const refreshResponse = await axios.post(
      `${GATEWAY_URL}/users/refresh-token`,
      { accessToken, refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const newAccessToken = refreshResponse.data.data?.token || refreshResponse.data.token;
    const newRefreshToken = refreshResponse.data.data?.refreshToken || refreshResponse.data.refreshToken;

    if (newAccessToken && newRefreshToken) {
      saveTokens(newAccessToken, newRefreshToken);
      onTokenRefreshed(newAccessToken);
      return newAccessToken;
    }
    return null;
  } catch (err) {
    logger.warn('Proactive token refresh failed, will retry on 401', { error: err });
    return null;
  } finally {
    isRefreshing = false;
  }
};

export const apiClient = axios.create({
  baseURL: GATEWAY_URL,
  // No withCredentials - Pure JWT approach
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// ─── Retry Configuration ───
const MAX_RETRIES = 2;
const RETRY_DELAYS = [1000, 2000]; // Exponential backoff

const isRetryableError = (error: any): boolean => {
  if (error.code === 'ECONNABORTED') return true; // Timeout
  if (!error.response) return true; // Network error
  if (error.response.status >= 500 && error.config?.method?.toLowerCase() === 'get') return true;
  return false;
};

const retryDelay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Endpoints where a 401 is a MEANINGFUL ANSWER, not an expired session.
 *
 * Sign-in itself answers with 401 in cases the caller must handle: the auth
 * service returns 401 + `needsChurchCreation` for a verified user who is not an
 * admin, which is the signal to retry in the member context and send them to
 * the Song Studio. Refreshing a token or bouncing to /login on those responses
 * throws the answer away (and shows a bogus "session expired"), so the refresh
 * branch below skips these paths and lets the error reach the caller intact.
 */
const AUTH_ENDPOINT_PATHS = [
  '/users/login',
  '/users/login/google',
  '/users/login/apple',
  '/users/register',
  '/users/refresh-token',
  '/users/logout',
  '/verify-onboarding',
  '/resend-verification',
  '/forgot-password',
  '/reset-password',
];

const isAuthEndpoint = (url: string | undefined): boolean => {
  if (!url) return false;
  // Compare on the path only: the url may be relative or absolute, and may
  // carry a query string.
  const path = url.replace(/^https?:\/\/[^/]+/i, '').split('?')[0].replace(/\/+$/, '');
  return AUTH_ENDPOINT_PATHS.some((p) => path === p || path.endsWith(p));
};

// Request interceptor - add timing, correlation ID, and JWT token
apiClient.interceptors.request.use(
  async (config) => {
    // Add request metadata for debugging
    config.metadata = { 
      startTime: Date.now(),
      requestId: generateRequestId()
    };
    
    // Add correlation header for backend logging
    config.headers['X-Request-ID'] = config.metadata.requestId;
    
    // Skip token injection if an explicit Authorization header is already set
    // (e.g., onboarding token passed directly by createChurch/finalizeAdminSetup)
    if (!config.headers.Authorization) {
      // Proactively refresh token if it's about to expire
      const refreshedToken = await proactiveRefresh();
      
      // Automatically attach JWT token from localStorage
      const token = refreshedToken || getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    return config;
  },
  (error) => {
    logger.error('Request setup failed:', error.message);
    return Promise.reject(error);
  }
);

// Response interceptor - track timing, handle token refresh
apiClient.interceptors.response.use(
  (response) => {
    const duration = Date.now() - (response.config.metadata?.startTime || 0);
    const requestId = response.config.metadata?.requestId;

    // Warn about slow requests
    if (duration > 5000) {
      logger.warn(`Slow API request (${duration}ms): ${response.config.url}`, { requestId });
    }
    
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const duration = originalRequest?.metadata?.startTime 
      ? Date.now() - originalRequest.metadata.startTime 
      : 0;
    const requestId = originalRequest?.metadata?.requestId;
    
    // Retry on timeout and network errors (exponential backoff)
    if (isRetryableError(error) && originalRequest) {
      const retryCount = originalRequest._retryCount || 0;
      if (retryCount < MAX_RETRIES) {
        originalRequest._retryCount = retryCount + 1;
        const backoff = RETRY_DELAYS[retryCount] || 2000;
        logger.info(`Retrying request (${retryCount + 1}/${MAX_RETRIES}) after ${backoff}ms`, {
          requestId, url: originalRequest.url, reason: error.code || `HTTP ${error.response?.status}`
        });
        await retryDelay(backoff);
        return apiClient(originalRequest);
      }
    }

    // Handle 401 - try token refresh.
    // Sign-in / refresh / verification endpoints are excluded: a 401 there is the
    // endpoint's answer (e.g. `needsChurchCreation` for a non-admin member), and
    // treating it as an expired session would clear tokens and hard-redirect to
    // /login before the caller could act on it.
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint(originalRequest?.url)
    ) {
      originalRequest._retry = true;
      
      // If a refresh is already in progress, queue this request to retry after it completes
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh(
            (newToken: string) => {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(apiClient(originalRequest));
            },
            (err: unknown) => reject(err)
          );
        });
      }
      
      isRefreshing = true;
      
      try {
        const accessToken = getAccessToken();
        const refreshToken = getRefreshToken();
        
        if (!accessToken || !refreshToken) {
          // No tokens available, redirect to login
          logger.info('No tokens available, redirecting to login', { requestId });
          clearTokens();
          localStorage.setItem('logout_reason', 'Your session has expired. Please log in again.');
          window.location.href = '/login';
          return Promise.reject(error);
        }
        
        // Attempt token refresh
        logger.info('Access token expired, attempting refresh', { requestId });
        
        const refreshResponse = await axios.post(
          `${GATEWAY_URL}/users/refresh-token`,
          {
            accessToken: accessToken,
            refreshToken: refreshToken
          },
          {
            headers: { 'Content-Type': 'application/json' }
          }
        );
        
        const newAccessToken = refreshResponse.data.data?.token || refreshResponse.data.token;
        const newRefreshToken = refreshResponse.data.data?.refreshToken || refreshResponse.data.refreshToken;
        
        if (newAccessToken && newRefreshToken) {
          // Save new tokens
          saveTokens(newAccessToken, newRefreshToken);
          
          // Notify all queued requests with the new token
          onTokenRefreshed(newAccessToken);
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return apiClient(originalRequest);
        }
        
        throw new Error('Token refresh failed: No tokens in response');
      } catch (refreshError) {
        // Refresh failed, redirect to login
        logger.error('Token refresh failed, redirecting to login', { requestId, error: refreshError });
        clearTokens();
        // Reject all queued requests so their promises settle
        onTokenRefreshFailed(refreshError);
        localStorage.setItem('logout_reason', 'Your session has expired. Please log in again.');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Log errors with context (always logged, even in production)
    const errorContext = {
      requestId,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      status: error.response?.status,
      duration: `${duration}ms`,
      backendMessage: error.response?.data?.message || error.response?.data?.error,
      networkError: !error.response ? error.message : undefined
    };

    if (error.response) {
      logger.error(`API Error: ${errorContext.status} ${errorContext.method} ${errorContext.url}`, errorContext);
    } else {
      logger.error(`Network Error: ${errorContext.method} ${errorContext.url}`, errorContext);
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;
